import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import {alpha} from '@mui/system';
import {
  useBreadcrumb,
  useGo,
  useMutationMode,
  useNavigation,
  useResource,
  useRouterType,
  useToPath,
  useTranslate,
} from '@refinedev/core';
import {
  AutoSaveIndicator,
  type DeleteButtonProps,
  type EditProps,
  type ListButtonProps,
  type RefreshButtonProps,
  type SaveButtonProps,
} from '@refinedev/mui';
import {FC, ReactNode} from 'react';

import {DeleteButton} from '@components/buttons/delete';
import {SaveButton} from '@components/buttons/save';
import {CustomBreadcrumbs} from '@minimal/components/custom-breadcrumbs';

type CustomEditProps = Omit<EditProps, 'title'> & {
  title?: string | ReactNode;
};

export const CustomEdit: FC<CustomEditProps> = ({
  title,
  saveButtonProps: saveButtonPropsFromProps,
  mutationMode: mutationModeProp,
  recordItemId,
  children,
  deleteButtonProps: deleteButtonPropsFromProps,
  canDelete,
  resource: resourceFromProps,
  isLoading = false,
  dataProviderName,
  headerButtons,
  footerButtonProps,
  footerButtons,
  autoSaveProps,
}) => {
  const translate = useTranslate();
  const {mutationMode: mutationModeContext} = useMutationMode();
  const mutationMode = mutationModeProp ?? mutationModeContext;

  const routerType = useRouterType();
  const go = useGo();
  const {list: legacyGoList} = useNavigation();

  const {resource, id: idFromParams, identifier} = useResource(resourceFromProps);

  const goListPath = useToPath({
    resource,
    action: 'list',
  });

  const id = recordItemId ?? idFromParams;

  const hasList = resource?.list && !recordItemId;
  const isDeleteButtonVisible =
    canDelete ?? ((resource?.meta?.canDelete ?? resource?.canDelete) || deleteButtonPropsFromProps);

  // @ts-ignore

  const {breadcrumbs} = useBreadcrumb();

  const listButtonProps: ListButtonProps | undefined = hasList
    ? {
        ...(isLoading ? {disabled: true} : {}),
        resource: routerType === 'legacy' ? resource?.route : identifier,
      }
    : undefined;

  const refreshButtonProps: RefreshButtonProps = {
    ...(isLoading ? {disabled: true} : {}),
    resource: routerType === 'legacy' ? resource?.route : identifier,
    recordItemId: id,
    dataProviderName,
  };

  const defaultHeaderButtons = (
    <Box display="flex" flexDirection="row" alignItems="center">
      {autoSaveProps && <AutoSaveIndicator {...autoSaveProps} />}
    </Box>
  );

  const deleteButtonProps: DeleteButtonProps | undefined = isDeleteButtonVisible
    ? ({
        ...(isLoading ? {disabled: true} : {}),
        resource: routerType === 'legacy' ? resource?.route : identifier,
        mutationMode,
        variant: 'outlined',
        onSuccess: () => {
          if (routerType === 'legacy') {
            legacyGoList(resource?.route ?? resource?.name ?? '');
          } else {
            go({to: goListPath});
          }
        },
        recordItemId: id,
        dataProviderName,
        ...deleteButtonPropsFromProps,
      } as const)
    : undefined;

  const saveButtonProps: SaveButtonProps = {
    ...(isLoading ? {disabled: true} : {}),
    ...saveButtonPropsFromProps,
  };

  const defaultFooterButtons = (
    <>
      {isDeleteButtonVisible && <DeleteButton {...deleteButtonProps} />}
      <SaveButton {...saveButtonProps} />
    </>
  );

  return (
    <>
      <CustomBreadcrumbs
        heading={title?.toString() ?? translate(`${resource?.meta?.prefix}.titles.edit`)}
        links={breadcrumbs.map((item) => ({
          ...item,
          name: item.label,
        }))}
        action={
          headerButtons
            ? typeof headerButtons === 'function'
              ? headerButtons({
                  defaultButtons: defaultHeaderButtons,
                  listButtonProps,
                  refreshButtonProps,
                })
              : headerButtons
            : defaultHeaderButtons
        }
        sx={{mb: {xs: 3, md: 5}}}
      />
      {isLoading && (
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: (theme) => theme.zIndex.drawer + 1,
            // this is needed to support custom themes, dark mode etc.
            bgcolor: (theme) => alpha(theme.palette.background.paper, 0.4),
          }}
        >
          <CircularProgress />
        </Box>
      )}
      {children}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'flex-end',
          gap: '16px',
          padding: '16px',
        }}
        {...(footerButtonProps ?? {})}
      >
        {footerButtons
          ? typeof footerButtons === 'function'
            ? footerButtons({
                defaultButtons: defaultFooterButtons,
                deleteButtonProps,
                saveButtonProps,
              })
            : footerButtons
          : defaultFooterButtons}
      </Box>
    </>
  );
};
